import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ActionMenu, type ActionMenuEntry } from "@/components/admin/action-menu";
import { AlertBanner } from "@/components/admin/alert-banner";
import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { AdminIcon } from "@/components/admin/icon";
import { AdminLineChart } from "@/components/admin/line-chart";
import { PageHead } from "@/components/admin/page-head";
import { Segmented } from "@/components/admin/segmented";
import { Tag } from "@/components/admin/tag";
import { useConfirm, useToast } from "@/components/ui/feedback";
import { Modal, ModalFooter } from "@/components/ui/modal";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import { usePermissions } from "@/hooks/use-permissions";
import {
	type Banner,
	type BannerPayload,
	type BannerStatus,
	type BannerTemplate,
	bannersApi,
	PLACEMENT_LABELS,
	TEMPLATE_LABELS,
} from "@/lib/marketing/banners-api";

import { BannerFormModal } from "./banner-form";
import { BannerPreview } from "./banner-preview";

const CLE = ["marketing", "banners"] as const;

const STATUT: Record<
	BannerStatus,
	{ label: string; variant: "success" | "info" | "neutral" | "warning" }
> = {
	active: { label: "En ligne", variant: "success" },
	scheduled: { label: "Planifiée", variant: "info" },
	paused: { label: "Suspendue", variant: "warning" },
	draft: { label: "Brouillon", variant: "neutral" },
	archived: { label: "Archivée", variant: "neutral" },
};

const nombre = (n: number) => n.toLocaleString("fr-FR");

const periode = (b: Banner): string => {
	const d = (iso: string | null) =>
		iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }) : null;
	const debut = d(b.startsAt);
	const fin = d(b.endsAt);
	if (debut && fin) return `${debut} → ${fin}`;
	if (fin) return `Jusqu'au ${fin}`;
	if (debut) return `Dès le ${debut}`;
	return "Permanent";
};

type Filtre = "all" | "active" | "scheduled" | "draft" | "archived";

/**
 * Bannières de la vitrine.
 *
 * Cet écran remplace l'édition directe du carrousel, qui ne connaissait ni
 * période, ni cible, ni chiffres : une bannière de soldes finie restait en
 * ligne jusqu'à ce que quelqu'un pense à la retirer, et rien ne disait si
 * elle avait servi à quelque chose.
 */
export function AdminMarketingBannersPage() {
	const qc = useQueryClient();
	const toast = useToast();
	const confirm = useConfirm();
	const { hasAny } = usePermissions();
	const peutEcrire = hasAny("MARKETING.CMS.WRITE");
	const peutImporter = hasAny("SYSTEM.SETTINGS.WRITE");

	const [filtre, setFiltre] = useState<Filtre>("all");
	const [choixModele, setChoixModele] = useState(false);
	const [formulaire, setFormulaire] = useState<{
		banner: Banner | null;
		template: BannerTemplate;
	} | null>(null);
	const [stats, setStats] = useState<Banner | null>(null);

	const { data, isLoading } = useQuery({
		queryKey: [...CLE, "list"],
		queryFn: () => bannersApi.list(),
	});
	const { data: apercu } = useQuery({
		queryKey: [...CLE, "overview"],
		queryFn: () => bannersApi.overview(),
	});

	const rafraichir = () => {
		void qc.invalidateQueries({ queryKey: CLE });
	};
	const echec = (e: unknown) => toast.error(extractErrorMessage(e));

	const creer = useMutation({
		mutationFn: (p: BannerPayload & { template: BannerTemplate; title: string }) =>
			bannersApi.create(p),
		onSuccess: () => {
			toast.success("Bannière créée en brouillon");
			setFormulaire(null);
			rafraichir();
		},
		onError: echec,
	});

	const modifier = useMutation({
		mutationFn: ({ id, payload }: { id: string; payload: BannerPayload }) =>
			bannersApi.update(id, payload),
		onSuccess: () => {
			toast.success("Bannière mise à jour");
			setFormulaire(null);
			rafraichir();
		},
		onError: echec,
	});

	const statut = useMutation({
		mutationFn: ({ id, status }: { id: string; status: BannerStatus }) =>
			bannersApi.setStatus(id, status),
		onSuccess: (_d, v) => {
			toast.success(
				v.status === "active"
					? "Bannière mise en ligne"
					: v.status === "paused"
						? "Bannière suspendue"
						: "Statut mis à jour",
			);
			rafraichir();
		},
		onError: echec,
	});

	const dupliquer = useMutation({
		mutationFn: (id: string) => bannersApi.duplicate(id),
		onSuccess: () => {
			toast.success("Bannière dupliquée en brouillon");
			rafraichir();
		},
		onError: echec,
	});

	const variante = useMutation({
		mutationFn: (id: string) => bannersApi.createVariant(id),
		onSuccess: () => {
			toast.success("Variante B créée — modifiez-la puis mettez-la en ligne");
			rafraichir();
		},
		onError: echec,
	});

	const gagnante = useMutation({
		mutationFn: (id: string) => bannersApi.pickWinner(id),
		onSuccess: () => {
			toast.success("Test clos : la variante retenue reste en ligne");
			rafraichir();
		},
		onError: echec,
	});

	const archiver = useMutation({
		mutationFn: (id: string) => bannersApi.archive(id),
		onSuccess: () => {
			toast.success("Bannière archivée");
			rafraichir();
		},
		onError: echec,
	});

	const importer = useMutation({
		mutationFn: () => bannersApi.importHero(),
		onSuccess: (r) => {
			toast.success(`${r.imported} volet(s) repris du carrousel`);
			rafraichir();
		},
		onError: echec,
	});

	const ordre = useMutation({
		mutationFn: (o: Array<{ id: string; sortOrder: number }>) =>
			bannersApi.reorder(o),
		onSuccess: () => {
			toast.success("Ordre du carrousel enregistré");
			rafraichir();
		},
		onError: echec,
	});

	const toutes = data?.items ?? [];
	const affichees = toutes.filter(
		(b) => filtre === "all" || b.status === filtre,
	);
	const carrousel = toutes
		.filter(
			(b) =>
				b.placement === "hero_homepage" &&
				(b.status === "active" || b.status === "scheduled") &&
				b.abGroup !== "B",
		)
		.sort((a, b) => a.sortOrder - b.sortOrder);

	const deplacer = (index: number, delta: number) => {
		const cible = index + delta;
		if (cible < 0 || cible >= carrousel.length) return;
		const copie = [...carrousel];
		[copie[index], copie[cible]] = [copie[cible], copie[index]];
		ordre.mutate(copie.map((b, i) => ({ id: b.id, sortOrder: i })));
	};

	const menu = (b: Banner): ActionMenuEntry[] => {
		const entrees: ActionMenuEntry[] = [
			{
				label: "Modifier",
				icon: "edit",
				disabled: !peutEcrire,
				onClick: () => setFormulaire({ banner: b, template: b.template }),
			},
			{
				label: "Voir les chiffres",
				icon: "chart",
				onClick: () => setStats(b),
			},
			{
				label: "Dupliquer",
				icon: "copy",
				disabled: !peutEcrire,
				onClick: () => dupliquer.mutate(b.id),
			},
		];

		if (b.status === "active") {
			entrees.push({
				label: "Suspendre",
				icon: "clock",
				disabled: !peutEcrire,
				onClick: () => statut.mutate({ id: b.id, status: "paused" }),
			});
		} else if (b.status !== "archived") {
			entrees.push({
				label: "Mettre en ligne",
				icon: "check",
				disabled: !peutEcrire,
				onClick: () => statut.mutate({ id: b.id, status: "active" }),
			});
		}

		if (!b.abGroup) {
			entrees.push({
				label: "Lancer un test A/B",
				icon: "upDown",
				disabled: !peutEcrire,
				onClick: () => variante.mutate(b.id),
			});
		} else {
			entrees.push({
				label: `Garder la variante ${b.abGroup}`,
				icon: "check",
				disabled: !peutEcrire,
				onClick: () => gagnante.mutate(b.id),
			});
		}

		entrees.push("sep", {
			label: "Archiver",
			icon: "trash",
			danger: true,
			disabled: !peutEcrire,
			onClick: () => {
				void confirm({
					title: "Archiver cette bannière ?",
					message: `« ${b.title} » sera retirée de la vitrine. Ses chiffres restent consultables.`,
					confirmLabel: "Archiver",
					tone: "danger",
				}).then((ok) => {
					if (ok) archiver.mutate(b.id);
				});
			},
		});

		return entrees;
	};

	return (
		<>
			<PageHead
				title="Bannières"
				sub="Carrousel d'accueil, encarts, ciblage et tests A/B"
				actions={
					peutEcrire && (
						<AdminButton variant="primary" onClick={() => setChoixModele(true)}>
							<AdminIcon n="plus" s={14} />
							Nouvelle bannière
						</AdminButton>
					)
				}
			/>

			{/* Module vide alors que le carrousel tourne : il faut le reprendre,
			    sinon des bannières en ligne n'apparaîtraient nulle part ici. */}
			{!isLoading && toutes.length === 0 && peutImporter && (
				<div style={{ marginBottom: 16 }}>
					<AlertBanner
						variant="info"
						title="Reprendre le carrousel existant"
						description="Les volets saisis dans l'ancien écran « Page d'accueil » ne sont pas encore gérés ici. La reprise les transforme en bannières, une seule fois."
						actions={
							<AdminButton
								variant="secondary"
								size="sm"
								disabled={importer.isPending}
								onClick={() => importer.mutate()}
							>
								{importer.isPending ? "Reprise…" : "Reprendre les volets"}
							</AdminButton>
						}
					/>
				</div>
			)}

			{apercu && (
				<div className="kpi-grid">
					<div className="kpi accent">
						<div className="kpi-l">En ligne</div>
						<div className="kpi-v tn">{apercu.active}</div>
						<span className="kpi-delta neutral">
							{apercu.scheduled} planifiée(s)
						</span>
					</div>
					<div className="kpi">
						<div className="kpi-l">Impressions · 30 j</div>
						<div className="kpi-v tn">{nombre(apercu.impressions30d)}</div>
					</div>
					<div className="kpi">
						<div className="kpi-l">Clics · 30 j</div>
						<div className="kpi-v tn">{nombre(apercu.clicks30d)}</div>
					</div>
					<div className="kpi forest">
						<div className="kpi-l">CTR moyen · 30 j</div>
						<div className="kpi-v tn">
							{apercu.ctr30d.toFixed(1)}
							<small>%</small>
						</div>
					</div>
				</div>
			)}

			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					marginBottom: 14,
					gap: 12,
					flexWrap: "wrap",
				}}
			>
				<Segmented
					value={filtre}
					onChange={setFiltre}
					options={[
						{ value: "all", label: "Toutes" },
						{ value: "active", label: "En ligne" },
						{ value: "scheduled", label: "Planifiées" },
						{ value: "draft", label: "Brouillons" },
						{ value: "archived", label: "Archivées" },
					]}
				/>
			</div>

			{isLoading ? (
				<div className="empty">Chargement…</div>
			) : affichees.length === 0 ? (
				<div className="empty">Aucune bannière dans ce filtre.</div>
			) : (
				<div className="banner-grid">
					{affichees.map((b) => {
						const st = STATUT[b.status];
						return (
							<div className="banner-card" key={b.id}>
								<BannerPreview b={b} />
								<div className="bc-body">
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "flex-start",
											gap: 8,
										}}
									>
										<div style={{ minWidth: 0 }}>
											<div className="bc-name">{b.title}</div>
											<div className="bc-meta">
												<Tag variant="neutral" noDot style={{ marginRight: 6 }}>
													{TEMPLATE_LABELS[b.template].name}
												</Tag>
												{periode(b)} · {PLACEMENT_LABELS[b.placement]}
												{b.segmentLabel ? ` · ${b.segmentLabel}` : ""}
											</div>
										</div>
										<div
											style={{
												display: "flex",
												gap: 6,
												alignItems: "center",
												flexShrink: 0,
											}}
										>
											{b.abGroup && (
												<Tag variant="info" noDot>
													Variante {b.abGroup}
												</Tag>
											)}
											<Tag variant={st.variant}>{st.label}</Tag>
										</div>
									</div>
									<div className="bc-stats">
										<div className="bc-stat">
											<div className="v tn">{nombre(b.impressions)}</div>
											<div className="l">Impressions</div>
										</div>
										<div className="bc-stat">
											<div className="v tn">{b.ctr.toFixed(1)}%</div>
											<div className="l">CTR</div>
										</div>
										<div className="bc-stat">
											<div className="v tn">{nombre(b.conversions)}</div>
											<div className="l">Conversions</div>
										</div>
									</div>
								</div>
								<div className="bc-foot">
									{peutEcrire && (
										<AdminButton
											variant="ghost"
											size="sm"
											onClick={() =>
												setFormulaire({ banner: b, template: b.template })
											}
										>
											<AdminIcon n="edit" s={12} />
											Modifier
										</AdminButton>
									)}
									<AdminButton
										variant="ghost"
										size="sm"
										onClick={() => setStats(b)}
									>
										<AdminIcon n="chart" s={12} />
										Chiffres
									</AdminButton>
									<span style={{ marginLeft: "auto" }}>
										<ActionMenu items={menu(b)} />
									</span>
								</div>
							</div>
						);
					})}
				</div>
			)}

			{carrousel.length > 0 && (
				<Card style={{ marginTop: 16 }}>
					<CardHead
						title="Ordre du carrousel d'accueil"
						sub="Le premier volet est celui que le visiteur voit en arrivant"
					/>
					<CardBody>
						{carrousel.map((b, i) => (
							<div
								key={b.id}
								style={{
									display: "flex",
									alignItems: "center",
									gap: 12,
									padding: "10px 0",
									borderBottom:
										i < carrousel.length - 1
											? "1px solid var(--a-border)"
											: "none",
								}}
							>
								<span className="tn" style={{ width: 18, fontWeight: 700 }}>
									{i + 1}
								</span>
								<span
									style={{
										width: 40,
										height: 24,
										borderRadius: 4,
										background: b.color,
										flexShrink: 0,
									}}
								/>
								<strong style={{ flex: 1, fontSize: 12.5, minWidth: 0 }}>
									{b.title}
								</strong>
								<span className="muted" style={{ fontSize: 11 }}>
									{b.durationSec} s
								</span>
								<Tag variant={STATUT[b.status].variant}>
									{STATUT[b.status].label}
								</Tag>
								{peutEcrire && (
									<span style={{ display: "inline-flex", gap: 4 }}>
										<AdminButton
											variant="ghost"
											size="sm"
											iconOnly
											aria-label="Monter"
											title="Monter"
											disabled={i === 0 || ordre.isPending}
											onClick={() => deplacer(i, -1)}
										>
											{/* Pas de chevron vers le haut dans le jeu d'icônes :
											    celui du bas, retourné. */}
											<span style={{ display: "flex", transform: "rotate(180deg)" }}>
												<AdminIcon n="chevD" s={13} />
											</span>
										</AdminButton>
										<AdminButton
											variant="ghost"
											size="sm"
											iconOnly
											aria-label="Descendre"
											title="Descendre"
											disabled={i === carrousel.length - 1 || ordre.isPending}
											onClick={() => deplacer(i, 1)}
										>
											<AdminIcon n="chevD" s={13} />
										</AdminButton>
									</span>
								)}
							</div>
						))}
					</CardBody>
				</Card>
			)}

			{choixModele && (
				<ChoixModeleModal
					onClose={() => setChoixModele(false)}
					onPick={(t) => {
						setChoixModele(false);
						setFormulaire({ banner: null, template: t });
					}}
				/>
			)}

			{formulaire && (
				<BannerFormModal
					banner={formulaire.banner}
					template={formulaire.template}
					saving={creer.isPending || modifier.isPending}
					onClose={() => setFormulaire(null)}
					onSubmit={(payload) => {
						if (formulaire.banner) {
							modifier.mutate({ id: formulaire.banner.id, payload });
						} else {
							creer.mutate(payload);
						}
					}}
				/>
			)}

			{stats && <StatsModal banner={stats} onClose={() => setStats(null)} />}
		</>
	);
}

/**
 * Choix du modèle, par l'image et non par le nom.
 *
 * Six modèles aux champs différents : les présenter en liste déroulante
 * obligeait à en essayer plusieurs pour comprendre lequel faisait quoi.
 */
function ChoixModeleModal({
	onClose,
	onPick,
}: {
	onClose: () => void;
	onPick: (t: BannerTemplate) => void;
}) {
	return (
		<Modal
			title="Choisir un modèle"
			subtitle="Chaque modèle a ses propres champs et son propre rendu"
			onClose={onClose}
			width={820}
		>
			<div
				style={{
					display: "grid",
					gridTemplateColumns: "repeat(auto-fit, minmax(min(230px, 100%), 1fr))",
					gap: 12,
				}}
			>
				{(
					Object.keys(TEMPLATE_LABELS) as BannerTemplate[]
				).map((id) => {
					const t = TEMPLATE_LABELS[id];
					return (
						<button
							key={id}
							type="button"
							onClick={() => onPick(id)}
							style={{
								textAlign: "left",
								border: "1px solid var(--a-border)",
								borderRadius: 10,
								overflow: "hidden",
								background: "var(--a-surface)",
								cursor: "pointer",
								padding: 0,
								fontFamily: "inherit",
							}}
						>
							<BannerPreview
								b={{
									template: id,
									color: "#C6563A",
									title: t.name,
									eyebrow: "Aperçu",
									ctaLabel: "Bouton",
									discount: "-30%",
									code: "CODE",
									thumbs: ["#D4A24C", "#1E4D3B", "#E8D9C1"],
									categorySlugs: ["mode", "beaute", "maison"],
								}}
							/>
							<div style={{ padding: "10px 12px" }}>
								<strong style={{ fontSize: 12.5 }}>{t.name}</strong>
								<div
									className="muted"
									style={{ fontSize: 11, lineHeight: 1.4, marginTop: 3 }}
								>
									{t.desc}
								</div>
							</div>
						</button>
					);
				})}
			</div>
		</Modal>
	);
}

/** Courbe et totaux d'une bannière sur trente jours. */
function StatsModal({
	banner,
	onClose,
}: {
	banner: Banner;
	onClose: () => void;
}) {
	const { data, isLoading } = useQuery({
		queryKey: [...CLE, "series", banner.id],
		queryFn: () => bannersApi.series(banner.id, 30),
	});

	return (
		<Modal
			title={`Chiffres · ${banner.title}`}
			subtitle="Trente derniers jours"
			onClose={onClose}
			width={640}
		>
			{isLoading ? (
				<div className="empty">Chargement…</div>
			) : !data || data.days.length === 0 ? (
				<div className="empty">
					Aucune mesure pour l'instant. Les impressions sont comptées par la
					vitrine dès que la bannière est en ligne.
				</div>
			) : (
				<>
					<div className="kpi-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
						<div className="kpi">
							<div className="kpi-l">Impressions</div>
							<div className="kpi-v tn">{nombre(data.total.impressions)}</div>
						</div>
						<div className="kpi">
							<div className="kpi-l">Clics</div>
							<div className="kpi-v tn">{nombre(data.total.clicks)}</div>
						</div>
						<div className="kpi">
							<div className="kpi-l">Conversions</div>
							<div className="kpi-v tn">{nombre(data.total.conversions)}</div>
						</div>
					</div>
					<div style={{ marginTop: 12 }}>
						<AdminLineChart
							data={data.days.map((d) => d.impressions)}
							labels={data.days.map((d, i) =>
								i % 5 === 0 ? d.day.slice(8) : "",
							)}
							color={banner.color}
							height={160}
						/>
					</div>
				</>
			)}
			<ModalFooter>
				<AdminButton variant="primary" onClick={onClose}>
					Fermer
				</AdminButton>
			</ModalFooter>
		</Modal>
	);
}
